import { array, concat, fn, get, hash } from '@ember/helper';
import { action } from '@ember/object';
import didUpdate from '@ember/render-modifiers/modifiers/did-update';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';
import changesetGet from 'ember-changeset/helpers/changeset-get';
import { focusTrap } from 'ember-focus-trap';
import { t } from 'ember-intl';
import sub from 'ember-math-helpers/helpers/sub';
import media from 'ember-responsive/helpers/media';
import { set } from 'ember-set-helper';
import { and, eq, gt, not, or } from 'ember-truth-helpers';
import BvBtn from 'volta/components/bv-btn';
import BvDropdownMenu from 'volta/components/bv-dropdown-menu';
import BvFiltersFilterTags from 'volta/components/bv-filters/filter-tags';
import BvFiltersSearchInput from 'volta/components/bv-filters/search-input';
import BvIcon from 'volta/components/bv-icon';
import BvInline from 'volta/components/bv-inline';
import BvPopover from 'volta/components/bv-popover';
import BvTabs from 'volta/components/bv-tabs';
import BvText from 'volta/components/bv-text';
import CollectionViewGroupTags from 'volta/components/collection-view/group-tags';
import CollectionViewSettings from 'volta/components/collection-view/settings';
import CollectionViewSortList from 'volta/components/collection-view/sort-list';
import CollectionViewUnsaved from 'volta/components/collection-view/unsaved';
import ConfirmDeleteModal from 'volta/components/confirm-delete-modal';
import can from 'volta/helpers/can';
import classNames from 'volta/helpers/class-names';
import { collectionFiltersToFilterValues, viewIsLocked } from 'volta/models/collection-view';
import { DEFAULT_FILTER_ARRAY_SEPARATOR } from 'volta/utils/filters-utils';

import type Transition from '@ember/routing/transition';
import type { BufferedChangeset } from 'ember-changeset/types';
import type { IntlService } from 'ember-intl';
import type { EmberTableSort } from 'volta/components/bv-table';
import type CollectionView from 'volta/models/collection-view';
import type {
  ICollectionArgs,
  ICollectionFilter,
  ICollectionGroupOption,
  ICollectionProperty,
  ICollectionSortOption,
  ICollectionViewDefinition,
  ICollectionViewEditCmd,
  ICreateCollectionViewCmd,
  TCollectionType
} from 'volta/models/types/collection-view';
import type { TEntityType } from 'volta/models/types/entity-types';
import type BvFlashService from 'volta/services/bv-flash';
import type SessionUserService from 'volta/services/session-user';

const ICON_FROM_TYPE: Record<TCollectionType, string> = {
  TABLE: 'table-view',
  KANBAN: 'kanban-view',
  LIST: 'list',
  TIMELINE: 'timeline-view'
};

export interface CollectionViewSignature {
  Args: {
    collectionDefinition: ICollectionViewDefinition;
    createViewChangeset?: BufferedChangeset & ICreateCollectionViewCmd;
    editViewChangeset?: BufferedChangeset & ICollectionViewEditCmd;
    enableAttributes?: boolean;
    enableGrouping?: boolean;
    exportCurrentLabel?: string;
    filtersClassNames?: string;
    groupByOptions: ICollectionGroupOption[];
    hasUnsavedChanges: boolean;
    isLoading: boolean;
    metadata?: object;
    queryParams: TQueryParams;
    selectedView?: CollectionView<ICollectionArgs>;
    selectedViewId: string;
    sortOptions: ICollectionSortOption[];
    sorts: EmberTableSort[];
    views?: CollectionView<ICollectionArgs>[];
    forceMobile?: boolean;
    settingsModalBelowAppHeader?: boolean;
    searchInputPlaceholder?: string;
    onCancelCreation: () => void;
    onCollectionTypeChange: (type: TCollectionType) => void;
    onComponentArgsChange?: (args: ICollectionArgs) => void;
    onCreate: () => Promise<void>;
    onDelete: () => Promise<void>;
    onDiscard?: () => void;
    onDuplicate: () => Promise<void>;
    onExportCurrent?: () => void;
    onFilterValuesChange: (filter: TFilterValue, field: string) => void;
    onGroupByChange: (group: ICollectionGroupOption, checked: boolean) => void;
    onGroupByOptionsReorder: (groups: ICollectionGroupOption[]) => void;
    onInitCreate: () => void;
    onPropertyChange: (props: ICollectionProperty[]) => void;
    onQueryParamsChange: (element: HTMLElement, [queryParams]: [TQueryParams]) => void;
    onSave?: () => Promise<void>;
    onSearch: (values: TFilterValues, search: string) => void;
    onSortDirectionChange: (option: ICollectionSortOption) => void;
    onSortOptionsReorder: (options: ICollectionSortOption[]) => void;
    onSortSelectionChange: (option: ICollectionSortOption, checked: boolean) => void;
    selectView: (viewId: string) => void;
    sendCollectionQueryParams: (isInitial?: boolean) => Transition | undefined;
  };
  Blocks: {
    filtersSecondary: [{ openEditModal: (section?: string) => void; filterValues: TFilterValues }];
  };
  Element: HTMLDivElement;
}

export default class CollectionViewComponent extends Component<CollectionViewSignature> {
  @service sessionUser!: SessionUserService;
  @service intl!: IntlService;
  @service bvFlash!: BvFlashService;

  @tracked settingsSection = 'general';
  @tracked editModalIsOpened = false;
  @tracked createModalIsOpened = false;
  @tracked confirmDeleteModalIsOpened = false;

  get orderedViews() {
    return this.args.views ?? [];
  }

  get isGroupMin() {
    const { groupMin } = this.args.collectionDefinition;
    if (!groupMin) {
      return false;
    }

    return this.args.groupByOptions.filter((option) => option.selected).length <= groupMin;
  }

  get filterValues() {
    const showForTags: ICollectionFilter[] = this.args.editViewChangeset?.get('filters') ?? [];
    return collectionFiltersToFilterValues(showForTags.filter((f) => f.property !== 'q'));
  }

  get viewIsLocked() {
    if (this.args.selectedView) {
      return viewIsLocked(this.args.selectedView, this.sessionUser.userId);
    }
    return false;
  }

  get selectedSortOptions() {
    return this.args.sortOptions.filter((o) => o.selected === true);
  }

  get filters(): ICollectionFilter[] {
    return this.args.editViewChangeset?.get('filters') ?? [];
  }

  @cached
  get properties(): ICollectionProperty[] {
    return this.args.editViewChangeset?.get('properties') ?? [];
  }

  get showedTagsFilters() {
    return this.filters.filter((f) => f.property !== 'q');
  }

  get user() {
    return this.sessionUser.user;
  }

  get userId() {
    return this.user!.id;
  }

  get sortDirectionGlyph() {
    const glyphs = [
      ...new Set(this.selectedSortOptions.map((o) => (o.asc ? 'arrow-down' : 'arrow-up')))
    ];
    return glyphs.length === 2 ? 'import-export' : glyphs[0];
  }

  get selectedGroups() {
    return this.args.groupByOptions.filter((g) => g.selected);
  }

  get otherGroups() {
    return this.args.groupByOptions.filter((g) => !g.selected);
  }

  // Actions

  @action
  handleSelectViewTab(view?: string) {
    if (view) {
      this.args.selectView(view);
    }
  }

  @action
  openCreateModal() {
    this.args.onInitCreate();
    this.settingsSection = 'general';
    this.createModalIsOpened = true;
  }

  @action
  closeCreateModal() {
    this.args.onCancelCreation();
    this.createModalIsOpened = false;
  }

  @action
  openEditModal(section: string = 'general') {
    this.settingsSection = section;
    this.editModalIsOpened = true;
  }

  get selectedViewIndex() {
    return this.orderedViews.findIndex((v) => v.idOrDefault === this.args.selectedViewId);
  }

  @action
  handleTabMove(collectionViewId: string, direction: 'left' | 'right') {
    const { orderedViews } = this;
    const cvIds = orderedViews.map((v) => v.idOrDefault);
    const index = cvIds.findIndex((id) => id === collectionViewId);
    const newIndex =
      direction === 'right' ? Math.min(index + 1, cvIds.length - 1) : Math.max(index - 1, 0);

    if (index !== newIndex) {
      [cvIds[index], cvIds[newIndex]] = [cvIds[newIndex]!, cvIds[index]!];
      this.sessionUser.setCollectionViewsOrder(
        this.args.collectionDefinition.resourceName.plural as TEntityType,
        cvIds
      );
    }
  }

  @action
  handleViewDeleted() {
    if (this.args.views?.length !== 1) {
      this.args.onDelete();
      this.editModalIsOpened = false;
      this.confirmDeleteModalIsOpened = false;
    }
  }

  @action
  handleRemoveGroup(group: ICollectionGroupOption) {
    return this.args.onGroupByOptionsReorder?.(
      this.args.groupByOptions.filter((g) => g.value != group.value)
    );
  }

  @action
  async handleCopyLinkToView() {
    await navigator.clipboard.writeText(window.location.href);
    this.bvFlash.success(this.intl.t('copied'));
  }

  // Helpers

  iconFromType = (view: CollectionView) => {
    const { types } = this.args.collectionDefinition;
    if (types.length < 2) {
      return undefined;
    }
    return view && ICON_FROM_TYPE[view.collectionType];
  };

  searchValue = (filters: ICollectionFilter[]) => {
    const q = filters.find((filter) => filter.property === 'q');
    return q?.values.join(DEFAULT_FILTER_ARRAY_SEPARATOR) ?? '';
  };

  <template>
    {{#let
      (or @forceMobile (media 'isMobile'))
      @editViewChangeset.sortBy.length
      (can 'CreateCollectionView' 'collectionViews')
      (can 'UpdateCollectionView' 'collectionViews')
      as |isMobile sortByLength canCreate canUpdate|
    }}
      {{! template-lint-disable no-at-ember-render-modifiers }}
      <div
        class='bv-collection-view u-pb-2'
        ...attributes
        {{didUpdate @onQueryParamsChange @queryParams}}
      >
        <div class='bv-collection-view__tabs'>
          <BvTabs @selectedTab={{@selectedViewId}} @isLoading={{@isLoading}}>
            <:default as |Tab|>
              {{#each this.orderedViews key='idOrDefault' as |v|}}
                {{#let (eq v.idOrDefault @selectedViewId) as |isSelected|}}
                  <BvDropdownMenu
                    @disabled={{not isSelected}}
                    @sections={{array
                      (hash
                        items=(array
                          (unless
                            (eq this.selectedViewIndex (sub this.orderedViews.length 1))
                            (hash
                              content=(t 'collectionViewsPage.moveRight')
                              icon='arrow-forward'
                              onClick=(fn this.handleTabMove v.idOrDefault 'right')
                            )
                          )
                          (unless
                            (eq this.selectedViewIndex 0)
                            (hash
                              content=(t 'collectionViewsPage.moveLeft')
                              icon='arrow-back'
                              onClick=(fn this.handleTabMove v.idOrDefault 'left')
                            )
                          )
                        )
                      )
                      (hash
                        items=(array
                          (unless
                            v.isLocked
                            (hash
                              content=(t 'rename')
                              icon='edit'
                              onClick=(fn this.openEditModal 'general')
                            )
                          )
                          (hash
                            content=(t 'collectionViewsPage.edit')
                            icon='options'
                            onClick=(fn this.openEditModal 'general')
                          )
                        )
                      )
                      (hash
                        items=(array
                          (if
                            @onExportCurrent
                            (hash
                              content=(or @exportCurrentLabel (t 'poWorkbench.export'))
                              icon='download-outline'
                              onClick=@onExportCurrent
                            )
                          )
                          (hash
                            content=(t 'collectionViewsPage.copyLink')
                            icon='link-outline'
                            onClick=this.handleCopyLinkToView
                          )
                        )
                      )
                      (hash
                        items=(array
                          (if
                            canCreate
                            (hash content=(t 'duplicate') icon='copy-outline' onClick=@onDuplicate)
                          )
                          (if
                            (and
                              (not this.viewIsLocked)
                              v.id
                              (can 'DeleteCollectionView' 'collectionViews')
                            )
                            (hash
                              content=(t 'delete')
                              icon='trash-outline'
                              onClick=(set this 'confirmDeleteModalIsOpened' true)
                            )
                          )
                        )
                      )
                    }}
                  >
                    <:trigger as |popover|>
                      <Tab
                        @id={{v.idOrDefault}}
                        @iconLeft={{this.iconFromType v}}
                        @iconRight={{if v.isLocked 'lock-closed'}}
                        @iconRightColor='severe'
                        {{! @glint-expect-error: popover.toggle doesn't take the viewId as an argument }}
                        @onClick={{if isSelected popover.toggle @selectView}}
                        {{popover.trigger}}
                      >
                        {{if (and v.id isSelected) @editViewChangeset.name v.name}}
                      </Tab>
                    </:trigger>
                  </BvDropdownMenu>
                {{/let}}
              {{/each}}
              {{#if canCreate}}
                <BvBtn
                  class='u-ml-2'
                  @tooltipText={{t 'collectionViewsPage.create'}}
                  @onClick={{this.openCreateModal}}
                  @iconLeft='add'
                  @subtle={{true}}
                />
              {{/if}}
            </:default>
            <:secondary>
              <div class='u-d-flex u-align-items'>
                {{#if @hasUnsavedChanges}}
                  <CollectionViewUnsaved
                    @onDiscard={{@onDiscard}}
                    @onSave={{if (or canCreate canUpdate) @onSave}}
                    @isDefault={{or (not @selectedView.id) this.viewIsLocked}}
                    class='u-mr-4 u-animation-sheet-right-open'
                  />
                {{/if}}
              </div>
              <BvBtn
                @iconLeft='settings-outline'
                @tooltipText={{t 'collectionViewsPage.edit'}}
                @subtle={{true}}
                @onClick={{fn this.openEditModal 'general'}}
                @testName='view-options'
              />
            </:secondary>
          </BvTabs>
        </div>
        <div class={{classNames 'bv-collection-view__filters u-pt-4 u-pb-2' @filtersClassNames}}>
          <BvInline @blockAlign='center' @wrap={{false}} @gap='2'>
            <BvInline @blockAlign='center' @gap='2' class='u-flex-1'>
              <BvFiltersSearchInput
                class='u-flex-fill'
                @filterValues={{this.filterValues}}
                @resourceName={{@collectionDefinition.resourceName}}
                @onSearchChange={{@onSearch}}
                @searchValue={{this.searchValue this.filters}}
                @placeholder={{@searchInputPlaceholder}}
              />
              <BvBtn
                @onClick={{fn this.openEditModal 'filter'}}
                @iconLeft={{if isMobile 'filter' 'add'}}
                @iconLeftColor={{if (and isMobile this.filters.length) 'primary'}}
                @text={{unless isMobile (t 'collectionViewsPage.addFilter')}}
                @tooltipText={{if isMobile (t 'collectionViewsPage.addFilter')}}
              />
            </BvInline>
            <div>
              <BvPopover @size='sm' @isMenu={{true}} @horizontalPosition='right' as |popover|>
                <BvBtn
                  @onClick={{popover.toggle}}
                  @disclosure={{true}}
                  @iconLeft={{if isMobile 'import-export'}}
                  @iconLeftColor={{if (and isMobile sortByLength) 'primary'}}
                  {{popover.trigger}}
                >
                  {{#unless isMobile}}
                    <BvText @text={{t 'pagination.sortBy'}} @color='subdued' class='u-mr-1' />
                    <BvText
                      {{! @glint-expect-error: get returns unknow type but we know it's a string }}
                      @text={{if
                        (or (eq sortByLength 1) (eq this.selectedSortOptions.length 1))
                        (get this.selectedSortOptions '0.label')
                        (if (gt sortByLength 1) (concat sortByLength ' ' (t 'attributes')))
                      }}
                    />
                    <BvIcon @glyph={{this.sortDirectionGlyph}} class='u-ml-1' />
                  {{/unless}}
                </BvBtn>
                <popover.Content
                  {{focusTrap isActive=popover.isOpen focusTrapOptions=popover.focusTrapOptions}}
                >
                  <popover.Body @scrollable={{true}}>
                    <CollectionViewSortList
                      @onDirectionChange={{@onSortDirectionChange}}
                      @onSelectionChange={{@onSortSelectionChange}}
                      @onReorderOptions={{@onSortOptionsReorder}}
                      @options={{@sortOptions}}
                    />
                  </popover.Body>
                </popover.Content>
              </BvPopover>
            </div>
            {{#if (has-block 'filtersSecondary')}}
              {{yield
                (hash openEditModal=this.openEditModal filterValues=this.filterValues)
                to='filtersSecondary'
              }}
            {{/if}}
          </BvInline>
        </div>
        <BvInline
          @gap='3'
          @align='space-between'
          @blockAlign='center'
          class={{classNames 'u-d-none u-d-md-flex' @filtersClassNames}}
        >
          <BvFiltersFilterTags
            @resourceName={{@collectionDefinition.resourceName}}
            @filterValues={{this.filterValues}}
            @filterDefinitions={{@collectionDefinition.filters}}
            @onFilterValuesChange={{@onFilterValuesChange}}
          />
          {{#if @enableGrouping}}
            <BvInline @gap='2' @blockAlign='center'>
              <BvText @text={{t 'group'}} @variant='heading-sm' @color='subdued' />
              <CollectionViewGroupTags
                @groups={{this.selectedGroups}}
                @onRemoveGroup={{this.handleRemoveGroup}}
                @onReorder={{@onGroupByOptionsReorder}}
                @isGroupMin={{this.isGroupMin}}
              />
              <BvBtn
                @size='sm'
                @subtle={{true}}
                @iconLeft='arrow-forward'
                @onClick={{fn this.openEditModal 'groupBy'}}
              />
            </BvInline>
          {{/if}}

        </BvInline>
      </div>
      {{#if this.editModalIsOpened}}
        <CollectionViewSettings
          @view={{@selectedView}}
          {{! @glint-expect-error: createViewChangeset is deinfed in create mode }}
          @changeset={{@editViewChangeset}}
          @onClose={{set this 'editModalIsOpened' false}}
          @settingsSection={{this.settingsSection}}
          @collectionDefinition={{@collectionDefinition}}
          @onFilterChange={{@onFilterValuesChange}}
          @onSortDirectionChange={{@onSortDirectionChange}}
          @onSortSelectionChange={{@onSortSelectionChange}}
          @onSortOptionsReorder={{@onSortOptionsReorder}}
          @sortOptions={{@sortOptions}}
          @groupByOptions={{@groupByOptions}}
          @onGroupByOptionsReorder={{@onGroupByOptionsReorder}}
          @onPropertyChange={{@onPropertyChange}}
          @onGroupByChange={{@onGroupByChange}}
          @onComponentArgsChange={{@onComponentArgsChange}}
          @onCollectionTypeChange={{@onCollectionTypeChange}}
          @onEdit={{@onSave}}
          @onDelete={{set this 'confirmDeleteModalIsOpened' true}}
          @isCreateMode={{false}}
          @enableGrouping={{@enableGrouping}}
          @enableAttributes={{@enableAttributes}}
          @belowAppHeader={{@settingsModalBelowAppHeader}}
        />
      {{/if}}
      {{#if this.createModalIsOpened}}
        <CollectionViewSettings
          @view={{@selectedView}}
          {{! @glint-expect-error: createViewChangeset is deinfed in create mode }}
          @changeset={{@createViewChangeset}}
          @onClose={{this.closeCreateModal}}
          @settingsSection={{this.settingsSection}}
          @collectionDefinition={{@collectionDefinition}}
          @onCreate={{@onCreate}}
          @onSortDirectionChange={{@onSortDirectionChange}}
          @onSortSelectionChange={{@onSortSelectionChange}}
          @onSortOptionsReorder={{@onSortOptionsReorder}}
          @sortOptions={{@sortOptions}}
          @groupByOptions={{@groupByOptions}}
          @onGroupByOptionsReorder={{@onGroupByOptionsReorder}}
          @onPropertyChange={{@onPropertyChange}}
          @onGroupByChange={{@onGroupByChange}}
          @onComponentArgsChange={{@onComponentArgsChange}}
          @onCollectionTypeChange={{@onCollectionTypeChange}}
          @onFilterChange={{@onFilterValuesChange}}
          @isCreateMode={{true}}
          @enableGrouping={{@enableGrouping}}
          @belowAppHeader={{@settingsModalBelowAppHeader}}
        />
      {{/if}}
      {{#if this.confirmDeleteModalIsOpened}}
        <ConfirmDeleteModal
          @onCancel={{set this 'confirmDeleteModalIsOpened' false}}
          {{! @glint-expect-error: editViewChangeset is deinfed in create mode }}
          @name={{changesetGet @editViewChangeset 'name'}}
          @onDelete={{this.handleViewDeleted}}
        />
      {{/if}}
    {{/let}}
  </template>
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    CollectionView: typeof CollectionViewComponent;
    'collection-view': typeof CollectionViewComponent;
  }
}
